import { useRef, useState, useMemo } from "react";
import { Upload, FileText, X } from "lucide-react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import { Table, THead, TR, TH, TD } from "../ui/Table";
import { readCSVFile, stringifyCSV, downloadCSV } from "../../lib/csv";
import { formatUSD } from "../../lib/formatters";
import { toISO, today } from "../../lib/dateUtils";

// Target field names the CSV columns can be mapped to.
const FIELDS = [
  { value: "", label: "— Ignore —" },
  { value: "date", label: "Date *" },
  { value: "name", label: "Name *" },
  { value: "amount", label: "Amount *" },
  { value: "externalId", label: "Transaction ID" },
  { value: "externalStatus", label: "Status" },
  { value: "currency", label: "Currency" },
  { value: "category", label: "Category" },
  { value: "department", label: "Department" },
  { value: "notes", label: "Notes" },
];

// Lowercase header keyword → field. First match wins.
// IMPORTANT: order matters. Specific hints (Transaction ID, Status, Currency)
// must run BEFORE more generic ones (Name's "reference", Category's "type").
const HEADER_HINTS = [
  { match: /(transaction\s*id|^txn\s*id|^trans\s*id|^ref(\s*no|\s*number)?$|external\s*id|order\s*id|invoice\s*number|invoice\s*id)/i, field: "externalId" },
  { match: /(^status$|^state$|transaction\s*status|payment\s*status)/i, field: "externalStatus" },
  { match: /(^currency$|^ccy$|^iso\s*currency$)/i, field: "currency" },
  { match: /(transaction\s*date|paid\s*on|paid\s*date|when|^date$|^date\s|posting)/i, field: "date" },
  { match: /(description|memo|details|merchant|vendor|payee|item|^name$|reference)/i, field: "name" },
  { match: /(amount|debit|paid|cost|total|price|outflow|expense)/i, field: "amount" },
  { match: /(department|business\s*unit|^team$)/i, field: "department" },
  { match: /(category|type|class|account)/i, field: "category" },
  { match: /(note|memo|comment)/i, field: "notes" },
];

const CATEGORY_RULES = [
  { match: /stripe|fees|processing|paypal|wise.com|payment\s*processor/i, category: "Fees", costType: "opex" },
  { match: /rent|office|wework|coworking/i, category: "Rent", costType: "opex" },
  { match: /software|saas|subscription|notion|slack|figma|github|adobe|google\s*workspace|microsoft/i, category: "Software", costType: "opex" },
  { match: /salary|payroll|wages|contractor|freelance/i, category: "Payroll", costType: "cogs" },
  { match: /ads?|facebook|meta|google\s*ads|tiktok|youtube|advertising|marketing/i, category: "Marketing", costType: "opex" },
  { match: /domain|hosting|aws|vercel|netlify|server/i, category: "Hosting", costType: "opex" },
];

function detectField(header) {
  const h = String(header || "").trim().toLowerCase();
  if (!h) return "";
  for (const hint of HEADER_HINTS) {
    if (hint.match.test(h)) return hint.field;
  }
  return "";
}

function autoClassify(name) {
  const n = String(name || "").toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.match.test(n)) return { category: rule.category, costType: rule.costType };
  }
  return { category: "", costType: "opex" };
}

function parseAmount(raw) {
  if (raw == null) return 0;
  // Flip the sign to match the dashboard convention: charges are stored as
  // POSITIVE (a typical expense view), refunds as NEGATIVE.
  // Most bank CSVs show charges as negative (-20.30) and refunds as positive
  // (+20.30), so negating preserves the right meaning. (123.45) is also
  // recognised as -123.45 per accounting style and gets flipped to +123.45.
  const cleaned = String(raw).replace(/[$,\s]/g, "").replace(/\((.+)\)/, "-$1");
  const n = Number(cleaned);
  return Number.isFinite(n) ? -n : 0;
}

function parseDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : toISO(d);
}

const TEMPLATE_HEADERS = ["Date", "Name", "Amount", "Category", "Cost Class", "Department", "Offer", "Notes"];
const TEMPLATE_SAMPLE = {
  Date: toISO(today()),
  Name: "Notion subscription",
  Amount: "12.00",
  Category: "Software",
  "Cost Class": "OPEX",
  Department: "Email Outreach",
  Offer: "",
  Notes: "Monthly seat",
};

export default function ExpensesCSVImport({ offers = [], departments = [], onCommit }) {
  const fileRef = useRef(null);
  const [staged, setStaged] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await readCSVFile(file);
      const headers = parsed.headers || [];
      const mapping = {};
      headers.forEach((h) => { mapping[h] = detectField(h); });
      const rows = (parsed.records || []).map((rec) => {
        const get = (field) => {
          const header = headers.find((h) => mapping[h] === field);
          return header ? rec[header] : "";
        };
        const nameVal = String(get("name") || "").trim();
        const auto = autoClassify(nameVal);
        const csvCategory = String(get("category") || "").trim();
        const csvDept = String(get("department") || "").trim();
        // Match the CSV value case-insensitively to an existing department
        const matchedDept = departments.find((d) => d.toLowerCase() === csvDept.toLowerCase()) || csvDept;
        const statusVal = String(get("externalStatus") || "").trim();
        const currencyVal = String(get("currency") || "").trim().toUpperCase();
        return {
          _skip: false, // import everything by default; user can hide canceled via the page filter
          date: parseDate(get("date")),
          name: nameVal,
          amount: parseAmount(get("amount")),
          externalId: String(get("externalId") || "").trim() || null,
          externalStatus: statusVal || null,
          currency: currencyVal || "USD",
          category: csvCategory || auto.category,
          costType: auto.costType,
          offerId: "",
          department: matchedDept,
          notes: String(get("notes") || "").trim(),
        };
      });
      setStaged({ fileName: file.name, headers, mapping, records: parsed.records, rows });
    } catch (err) {
      alert("Could not parse CSV: " + err.message);
    }
    e.target.value = "";
  };

  // Recompute rows from records whenever the user changes a column mapping.
  const remapRows = (nextMapping) => {
    if (!staged) return;
    const rows = (staged.records || []).map((rec) => {
      const get = (field) => {
        const header = staged.headers.find((h) => nextMapping[h] === field);
        return header ? rec[header] : "";
      };
      const nameVal = String(get("name") || "").trim();
      const auto = autoClassify(nameVal);
      const csvCategory = String(get("category") || "").trim();
      const csvDept = String(get("department") || "").trim();
      const matchedDept = departments.find((d) => d.toLowerCase() === csvDept.toLowerCase()) || csvDept;
      return {
        _skip: false,
        date: parseDate(get("date")),
        name: nameVal,
        amount: parseAmount(get("amount")),
        category: csvCategory || auto.category,
        costType: auto.costType,
        offerId: "",
        department: matchedDept,
        notes: String(get("notes") || "").trim(),
      };
    });
    setStaged((s) => ({ ...s, mapping: nextMapping, rows }));
  };

  const setMapping = (header, field) => {
    if (!staged) return;
    // Unmap any other header currently pointing at this field (one-to-one)
    const next = { ...staged.mapping };
    if (field) {
      Object.keys(next).forEach((h) => {
        if (h !== header && next[h] === field) next[h] = "";
      });
    }
    next[header] = field;
    remapRows(next);
  };

  const updateRow = (idx, patch) => {
    setStaged((s) => ({
      ...s,
      rows: s.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const toggleSkip = (idx) => updateRow(idx, { _skip: !staged.rows[idx]._skip });

  const mappedFields = useMemo(() => {
    if (!staged) return new Set();
    return new Set(Object.values(staged.mapping).filter(Boolean));
  }, [staged]);
  const requiredMet = mappedFields.has("date") && mappedFields.has("name") && mappedFields.has("amount");

  const commit = () => {
    if (!staged || !requiredMet) return;
    // Allow negative (refunds) and positive (charges) amounts. Amount=0 is
    // accepted only for canceled rows (legit on bank statements). For non-
    // canceled rows, amount=0 means missing data → skip.
    const valid = staged.rows.filter((r) => {
      if (r._skip || !r.date || !r.name) return false;
      const canceledStatus = r.externalStatus && /cancel/i.test(r.externalStatus);
      const amountNonZero = Number(r.amount) !== 0;
      return amountNonZero || canceledStatus;
    });
    if (valid.length === 0) {
      alert("Nothing to import — every row is either skipped or missing required fields.");
      return;
    }
    onCommit(
      valid.map((r) => ({
        name: r.name,
        category: r.category || null,
        date: r.date,
        amount: Number(r.amount) || 0,
        notes: r.notes || null,
        offerId: r.offerId || null,
        costType: r.costType || "opex",
        department: r.department || null,
        status: "flagged",
        externalId: r.externalId || null,
        externalStatus: r.externalStatus || null,
        currency: r.currency || "USD",
      }))
    );
    setStaged(null);
  };

  const handleTemplate = () => {
    const csv = stringifyCSV([TEMPLATE_SAMPLE], TEMPLATE_HEADERS);
    downloadCSV("expenses-template.csv", csv);
  };

  const recurringOffers = offers.filter((o) => o.type === "recurring");
  const oneOffOffers = offers.filter((o) => o.type === "oneoff");

  const previewRows = staged?.rows?.slice(0, 50) || [];
  const totalAmount = staged?.rows?.filter((r) => !r._skip).reduce((s, r) => s + (Number(r.amount) || 0), 0) || 0;
  const validCount = staged?.rows?.filter((r) => {
    if (r._skip || !r.date || !r.name) return false;
    const canceledStatus = r.externalStatus && /cancel/i.test(r.externalStatus);
    const amountNonZero = Number(r.amount) !== 0;
    return amountNonZero || canceledStatus;
  }).length || 0;
  const skippedCount = staged?.rows?.filter((r) => r._skip).length || 0;
  const invalidCount = (staged?.rows?.length || 0) - validCount - skippedCount;

  return (
    <>
      <div className="inline-flex gap-1.5">
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Upload size={13} /> Upload CSV
        </Button>
        <button onClick={handleTemplate} className="text-xs text-brand-slate hover:text-brand-blue inline-flex items-center gap-1 px-2 py-1.5">
          <FileText size={13} /> Template
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
      </div>

      <Modal open={!!staged} onClose={() => setStaged(null)} title={`Import transactions${staged ? ` — ${staged.fileName}` : ""}`} size="xl">
        {staged && (
          <div className="space-y-3">
            <div className="bg-brand-blue-soft border border-blue-200 rounded-lg p-3 text-xs text-brand-blue-dark space-y-1">
              <div className="font-semibold">Column mapping</div>
              <p>Map each CSV column to one of the expense fields. <strong>Date</strong>, <strong>Name</strong>, and <strong>Amount</strong> are required.</p>
            </div>

            <div className="rounded-lg border border-gray-100 dark:border-[var(--border-soft)] p-3 bg-gray-50 dark:bg-[var(--bg-soft)]/30">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {staged.headers.map((h) => (
                  <div key={h} className="flex items-center gap-2">
                    <span className="text-xs text-brand-ink dark:text-[var(--text-primary)] font-medium truncate flex-1" title={h}>{h}</span>
                    <span className="text-xs text-brand-slate">→</span>
                    <select
                      value={staged.mapping[h] || ""}
                      onChange={(e) => setMapping(h, e.target.value)}
                      className="px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                    >
                      {FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {!requiredMet && (
                <p className="text-xs text-red-600 mt-2 font-medium">
                  Required: map a column to Date, Name, and Amount before importing.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 text-xs items-center">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{validCount} ready to import</span>
              {skippedCount > 0 && <span className="px-2 py-1 rounded bg-gray-100 text-brand-slate border border-gray-200">{skippedCount} skipped</span>}
              {invalidCount > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">{invalidCount} missing required fields</span>}
              <span className="ml-auto text-brand-slate">Total to import: <span className="font-medium text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(totalAmount)}</span></span>
            </div>

            <div className="max-h-[440px] overflow-auto border border-gray-100 dark:border-[var(--border-soft)] rounded-lg">
              <Table>
                <THead>
                  <TR>
                    <TH className="w-8"></TH>
                    <TH>Date</TH>
                    <TH>Name</TH>
                    <TH>Category</TH>
                    <TH>Status</TH>
                    <TH>Currency</TH>
                    <TH>Txn ID</TH>
                    <TH className="text-right">Amount</TH>
                  </TR>
                </THead>
                <tbody>
                  {previewRows.map((r, idx) => (
                    <TR key={idx} className={r._skip ? "opacity-40" : ""}>
                      <TD>
                        <button
                          type="button"
                          onClick={() => toggleSkip(idx)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title={r._skip ? "Restore" : "Skip"}
                        >
                          <X size={12} />
                        </button>
                      </TD>
                      <TD>
                        <input
                          type="date"
                          value={r.date || ""}
                          onChange={(e) => updateRow(idx, { date: e.target.value })}
                          className="px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                        />
                      </TD>
                      <TD>
                        <input
                          type="text"
                          value={r.name || ""}
                          onChange={(e) => updateRow(idx, { name: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                        />
                      </TD>
                      <TD>
                        <input
                          type="text"
                          value={r.category || ""}
                          onChange={(e) => updateRow(idx, { category: e.target.value })}
                          placeholder="—"
                          className="w-28 px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                        />
                      </TD>
                      <TD className="text-xs">
                        {r.externalStatus
                          ? (/cancel/i.test(r.externalStatus)
                              ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border bg-gray-100 text-gray-500 border-gray-200">{r.externalStatus}</span>
                              : <span className="text-brand-slate">{r.externalStatus}</span>)
                          : <span className="text-brand-slate">—</span>}
                      </TD>
                      <TD className="text-xs text-brand-slate">{r.currency || "USD"}</TD>
                      <TD className="text-[10px] font-mono text-brand-slate max-w-[120px] truncate" title={r.externalId || ""}>{r.externalId || "—"}</TD>
                      <TD>
                        <input
                          type="number"
                          step="0.01"
                          value={r.amount ?? 0}
                          onChange={(e) => updateRow(idx, { amount: Number(e.target.value) || 0 })}
                          className="w-24 px-2 py-1 text-right border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                        />
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
              {staged.rows.length > 50 && (
                <div className="text-xs text-brand-slate text-center py-2 border-t border-gray-100">
                  Showing first 50 of {staged.rows.length} rows. All non-skipped rows will be imported.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setStaged(null)}>Cancel</Button>
              <Button onClick={commit} disabled={!requiredMet || validCount === 0}>
                Import {validCount} expense{validCount === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}